import { useContext, useEffect, useRef, useState, type FormEvent } from "react";

import { ProjectContext } from "../app/projectContext";
import type { SldElements } from "../services/sldElements";

import { upsRunCreateRequestSchema, type UPSRunCreateRequest } from "../services/upsSizing";
import {
  upsBatteryTechnologySchema,
  upsPhaseConfigurationSchema,
  upsRedundancyModeSchema,
  upsTopologySchema,
  type UPSRedundancyMode,
} from "../services/upsSizingContract";
import { loadScenarioSchema } from "../services/loadDemandContract";
import { useStudyDraft } from "../hooks/useStudyDraft";
import { deriveStandbyUnits } from "../utils/sourceSizing";
import { describeValidationIssue } from "../utils/validationMessages";
import { SCENARIO_LABELS } from "./LoadRow";
import { OtherRevisionDraftNotice } from "./OtherRevisionDraftNotice";
import { SldElementPicker, sldElementsOf } from "./SldElementPicker";
import {
  UPS_BATTERY_TECHNOLOGY_LABELS,
  UPS_PHASE_CONFIGURATION_LABELS,
  UPS_REDUNDANCY_HINTS,
  UPS_REDUNDANCY_LABELS,
  UPS_STUDY_DRAFT_MODULE,
  UPS_STUDY_LABELS,
  UPS_TOPOLOGY_LABELS,
  buildUPSRunPayload,
  createInitialUPSStudyDraft,
  createUPSRatingDraft,
  seedUPSRatingIdCounter,
  upsStudyDraftSchema,
  type UPSRatingDraft,
  type UPSStudyDraft,
} from "./upsStudyDraft";

/** The first option of every choice: nothing is chosen for the engineer (A18 (h)). */
const CHOOSE = "Choose…";

export const OUTPUT_POWER_FACTOR_HINT =
  "From the UPS rating basis of the project, e.g. 0.9 means a 100 kVA module delivers " +
  "at most 90 kW.";

export const RUNTIME_HINT = "Required. Take it from the project design basis; it is never assumed.";

export const INVERTER_EFFICIENCY_HINT =
  "Leave blank if not yet known (flagged for review). Affects battery energy only.";

export const DERATING_HINT = "From the UPS module's certified derating data.";

export const DESCRIPTORS_NOTE = "Recorded with the study; they do not change any calculated value.";

type UPSStudyFormProps = {
  disabled?: boolean;
  onSubmit: (payload: UPSRunCreateRequest) => void | Promise<void>;
  // The revision's single-line diagram, when it has one (the page reads it): a UPS source
  // is picked from it. Nothing of the sizing is prefilled - the study selects the rating,
  // and the result is compared with the diagram afterwards (A28 (d)).
  sldElements?: SldElements | null | undefined;
};

// The UPS sizing study (EOS-03c). The form owns the draft. The redundant module
// count is derived from the redundancy mode rather than typed, so the rule the
// backend enforces cannot be broken here. The redundancy mode and the four
// descriptors start unchosen: the backend imposes no default (A18 (h)).
//
// The draft is kept under its own module key, "ups-sizing", next to the
// Transformer form's "transformer-sizing" and the DG form's "generator-sizing":
// all three belong to EOS-03, but no form ever reads, restores or deletes
// another's draft.
//
// The payload carries no project revision: the page adds the selected revision
// when it calls the API, exactly as the Transformer, DG, Load and Fault pages do.
export function UPSStudyForm({ disabled = false, onSubmit, sldElements }: UPSStudyFormProps) {
  // Tolerant of a missing provider (no project, no revision), like the fault form.
  const revisionId = useContext(ProjectContext)?.activeRevisionId() ?? null;
  // The entries outlive a reload, Back and the sidebar for as long as the tab is open.
  const { draft, setDraft, restored, clear, otherRevision, keepForThisRevision } =
    useStudyDraft<UPSStudyDraft>({
      module: UPS_STUDY_DRAFT_MODULE,
      createInitial: createInitialUPSStudyDraft,
      schema: upsStudyDraftSchema,
      revisionId,
    });
  const [validationError, setValidationError] = useState<string | null>(null);
  const [ratingToFocus, setRatingToFocus] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // A new entry sits among several fieldsets, so the cursor goes to it.
  useEffect(() => {
    if (ratingToFocus === null) {
      return;
    }
    formRef.current
      ?.querySelector<HTMLInputElement>(`input[name="rating-${ratingToFocus}"]`)
      ?.focus();
    setRatingToFocus(null);
  }, [ratingToFocus]);

  // Every edit keeps the derived redundant count in step with what it is made of.
  function updateStudy(patch: Partial<UPSStudyDraft>) {
    setDraft((current) => {
      const next = { ...current, ...patch };
      return {
        ...next,
        redundantModules: deriveStandbyUnits(next.redundancyMode, next.dutyModules),
      };
    });
  }

  function updateRating(id: string, value: string) {
    setDraft((current) => ({
      ...current,
      unitRatings: current.unitRatings.map((rating) =>
        rating.id === id ? { ...rating, value } : rating,
      ),
    }));
  }

  function addRating() {
    // A restored draft brings ids the counter of this page load has not handed out yet.
    seedUPSRatingIdCounter(draft.unitRatings.map((rating) => rating.id));
    const rating: UPSRatingDraft = createUPSRatingDraft();
    setDraft((current) => ({ ...current, unitRatings: [...current.unitRatings, rating] }));
    setRatingToFocus(rating.id);
  }

  function removeRating(id: string) {
    setDraft((current) => ({
      ...current,
      unitRatings: current.unitRatings.filter((rating) => rating.id !== id),
    }));
  }

  // Empties the form only; a result already on the page stays until "Clear results".
  function clearForm() {
    setValidationError(null);
    clear();
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setValidationError(null);

    const parsed = upsRunCreateRequestSchema.safeParse(buildUPSRunPayload(draft));

    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setValidationError(
        issue ? describeValidationIssue(issue, UPS_STUDY_LABELS) : "Review the UPS sizing inputs.",
      );
      return;
    }

    await onSubmit(parsed.data);
  }

  const redundancyMode = draft.redundancyMode as UPSRedundancyMode | "";

  const decimalField = (label: string, field: keyof UPSStudyDraft, name: string) => (
    <label>
      {label}
      <input
        inputMode="decimal"
        name={name}
        value={draft[field] as string}
        onChange={(event) => updateStudy({ [field]: event.target.value } as Partial<UPSStudyDraft>)}
      />
    </label>
  );

  // A descriptor: one select with "Choose…" first and the contract's values after it.
  const choiceField = <T extends string>(
    label: string,
    field: keyof UPSStudyDraft,
    name: string,
    options: readonly T[],
    labels: Record<T, string>,
  ) => (
    <label>
      {label}
      <select
        name={name}
        value={draft[field] as string}
        onChange={(event) => updateStudy({ [field]: event.target.value } as Partial<UPSStudyDraft>)}
      >
        <option value="">{CHOOSE}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {labels[option]}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <form aria-label="UPS sizing inputs" ref={formRef} onSubmit={handleSubmit}>
      {restored ? <p role="status">Draft restored from this session.</p> : null}
      {otherRevision ? (
        <OtherRevisionDraftNotice
          onKeep={keepForThisRevision}
          onClear={clearForm}
          disabled={disabled}
        />
      ) : null}

      <fieldset disabled={disabled}>
        <legend>Study definition</legend>

        <label>
          Study code
          <input
            name="studyCode"
            value={draft.code}
            onChange={(event) => updateStudy({ code: event.target.value })}
          />
        </label>

        <label>
          Study name
          <input
            name="studyName"
            value={draft.name}
            onChange={(event) => updateStudy({ name: event.target.value })}
          />
        </label>

        {sldElementsOf(sldElements, ["source"], ["UPS"]).length > 0 ? (
          <SldElementPicker
            label="Source from the diagram"
            name="sldElementCode"
            value={draft.sldElementCode}
            onChange={(sldElementCode) => updateStudy({ sldElementCode })}
            categories={["source"]}
            kinds={["UPS"]}
            elements={sldElements}
          />
        ) : null}

        <label>
          Jurisdiction profile
          <select
            name="jurisdictionProfile"
            value={draft.jurisdictionProfile}
            onChange={(event) => updateStudy({ jurisdictionProfile: event.target.value })}
          >
            <option value="IN">India (CEA Regulations, IS, CPWD)</option>
            <option value="IEC">IEC (international, no national layer)</option>
            <option value="UK">United Kingdom (BS 7671) - reference data pending</option>
            <option value="EU">European Union (HD 60364) - reference data pending</option>
            <option value="US">United States (NEC / NFPA 70) - reference data pending</option>
            <option value="AU_NZ">Australia / New Zealand (AS/NZS 3000) - reference data pending</option>
          </select>
        </label>
        <p>
          The sizing itself carries no profile-dependent value; the profile is recorded with
          the run and must match the design basis of the project revision.
        </p>

        <label>
          Study notes
          <input
            name="studyNotes"
            value={draft.notes}
            onChange={(event) => updateStudy({ notes: event.target.value })}
          />
        </label>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Load</legend>
        {decimalField("Critical load (kW)", "criticalLoadKw", "criticalLoadKw")}
        {decimalField("Load power factor", "loadPowerFactor", "loadPowerFactor")}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>UPS rating basis</legend>
        {decimalField(
          "UPS rated output power factor",
          "upsRatedOutputPowerFactor",
          "upsRatedOutputPowerFactor",
        )}
        <p data-output-power-factor-hint="true">{OUTPUT_POWER_FACTOR_HINT}</p>
        <fieldset disabled={disabled}>
          <legend>Module ratings</legend>
          {draft.unitRatings.map((rating, index) => (
            <div key={rating.id} data-rating-entry={rating.id}>
              <label>
                Module rating {index + 1}
                <input
                  inputMode="decimal"
                  name={`rating-${rating.id}`}
                  value={rating.value}
                  onChange={(event) => updateRating(rating.id, event.target.value)}
                />
              </label>
              {draft.unitRatings.length > 1 ? (
                <button type="button" onClick={() => removeRating(rating.id)}>
                  Remove rating {index + 1}
                </button>
              ) : null}
            </div>
          ))}
          <button type="button" onClick={addRating}>
            Add rating
          </button>
          <p data-ratings-hint="true">
            Enter the module ratings from the project design basis in ascending order.
          </p>
        </fieldset>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Battery</legend>
        {decimalField("Required runtime (min)", "requiredRuntimeMinutes", "requiredRuntimeMinutes")}
        <p data-runtime-hint="true">{RUNTIME_HINT}</p>
        {decimalField("Inverter efficiency", "inverterEfficiency", "inverterEfficiency")}
        <p data-inverter-efficiency-hint="true">{INVERTER_EFFICIENCY_HINT}</p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Factors</legend>
        {decimalField("Future growth factor", "futureGrowthFactor", "futureGrowthFactor")}
        {decimalField("Design margin factor", "designMarginFactor", "designMarginFactor")}
        {decimalField("Ambient derating factor", "ambientDeratingFactor", "ambientDeratingFactor")}
        {decimalField(
          "Altitude derating factor",
          "altitudeDeratingFactor",
          "altitudeDeratingFactor",
        )}
        <p data-derating-hint="true">{DERATING_HINT}</p>
        <p data-not-established-hint="true">
          Leave a factor blank if it is not established — the result will be marked
          Engineering review required.
        </p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Modules and redundancy</legend>

        <label>
          Duty modules
          <input
            inputMode="numeric"
            name="dutyModules"
            value={draft.dutyModules}
            onChange={(event) => updateStudy({ dutyModules: event.target.value })}
          />
        </label>

        {choiceField(
          "Redundancy mode",
          "redundancyMode",
          "redundancyMode",
          upsRedundancyModeSchema.options,
          UPS_REDUNDANCY_LABELS,
        )}

        <label>
          Redundant modules
          <input name="redundantModules" value={draft.redundantModules} readOnly />
        </label>
        {redundancyMode === "" ? null : (
          <p data-redundant-hint="true">{UPS_REDUNDANCY_HINTS[redundancyMode]}</p>
        )}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Descriptors (optional)</legend>
        <p data-descriptors-note="true">{DESCRIPTORS_NOTE}</p>
        {choiceField(
          "Topology",
          "topology",
          "topology",
          upsTopologySchema.options,
          UPS_TOPOLOGY_LABELS,
        )}
        {choiceField(
          "Phase configuration",
          "phaseConfiguration",
          "phaseConfiguration",
          upsPhaseConfigurationSchema.options,
          UPS_PHASE_CONFIGURATION_LABELS,
        )}
        {choiceField(
          "Battery technology",
          "batteryTechnology",
          "batteryTechnology",
          upsBatteryTechnologySchema.options,
          UPS_BATTERY_TECHNOLOGY_LABELS,
        )}
        {choiceField(
          "Scenario",
          "scenario",
          "scenario",
          loadScenarioSchema.options,
          SCENARIO_LABELS,
        )}
      </fieldset>

      {validationError ? <p role="alert">{validationError}</p> : null}

      <button disabled={disabled} type="submit">
        Calculate UPS size
      </button>
      <button disabled={disabled} type="button" onClick={clearForm}>
        Clear form
      </button>
    </form>
  );
}
